import { Link } from 'react-router-dom'
import { OPERATOR } from '../content/provenance'
import PolicyPage, { Section } from './PolicyPage'

export default function PrivacyPage() {
  return (
    <PolicyPage title="Privacy" intro="What information this site handles and where it stays, and how the site is built, served and protected.">
      <Section id="policy" heading="About this policy">
        <p>
          Mission Design System is a documentation site for a design system, built and operated by{' '}
          <a href={OPERATOR.url}>{OPERATOR.name}</a>. This policy covers missiondesign.us and the files it serves, and,
          like federal website policies, includes the site’s security notice. The <Link to="/disclaimer">Disclaimer</Link>{' '}
          explains the site’s relationship to the agencies it shows.
        </p>
      </Section>
      <Section id="collection" heading="Information collection and use">
        <p>
          The site asks for no personal information. It has no accounts, sign-in, mailing list or forms that send
          anything to a server. Search runs in your browser over an index of the documentation. The interactive
          examples, including the form templates, keep what you type in the page and discard it when you leave.
        </p>
        <p>Copying code puts it on your clipboard, and downloads are static files served from this site.</p>
      </Section>
      <Section id="automatic" heading="Information collected automatically">
        <p>
          The hosting service records the standard information a web server receives with each request: the
          address of the device making it, the date and time, the page or file requested, and the browser’s
          description of itself. {OPERATOR.name} uses these records only to keep the site running and protect it from
          abuse, and does not use them to identify or profile visitors.
        </p>
      </Section>
      <Section id="cookies" heading="Cookies and browser storage">
        <p>
          Google Analytics sets cookies to count visits. The design choices you make, such as a mission theme, light or dark, typefaces
          and the design labels, are saved in your browser’s local storage so they are still there on your next visit.
          They stay on your device and are never sent to the site.
        </p>
        <p>
          To remove them, use Reset in the Edit Design tools on the home page, or clear this site’s data in your
          browser’s settings.
        </p>
      </Section>
      <Section id="analytics" heading="Analytics and third parties">
        <p>
          The site uses Google Analytics to count visits and page views. Its pages, fonts,
          images and files all come from the site itself, which its Content Security Policy enforces.
        </p>
      </Section>
      <Section id="tools" heading="Starters and design tools">
        <p>
          The create-app command downloads the chosen agency’s hero photograph from this site when it creates a new
          project, and sends nothing about you or your project. The Figma plugin runs inside Figma with network access
          turned off; everything it draws is built into it.
        </p>
      </Section>
      <Section id="marks" heading="Agency names, seals and logos">
        <p>
          Agency names, seals, logos and photographs appear on this site to show how each mission theme looks. They
          belong to their agencies and photographers, are credited where they appear and on the Credits page, and their
          use here does not imply that any agency endorses this site. Federal law restricts the use of agency seals and
          insignia; a site that adopts a theme follows its agency’s rules for its own marks.
        </p>
      </Section>
      <Section id="security" heading="Security notice">
        <p>
          This site is provided as a public service. Its content is published only through the reviewed changes to its
          source repository. Attempts to change content on this site, to defeat its protections or to disrupt the
          service are prohibited and may be pursued under applicable law, including the Computer Fraud and Abuse Act.
        </p>
      </Section>
      <Section id="built" heading="How the site is built">
        <p>
          The site is a static application: every page, stylesheet, font, image and download is a file built from the
          source repository and served as it is. It has no server-side code, database, user accounts or forms that
          store information.
        </p>
      </Section>
      <Section id="served" heading="How it is served">
        <p>The site is served only over HTTPS, and every response carries headers that limit what a browser will do with it:</p>
        <ul>
          <li>Strict Transport Security, so browsers always connect over HTTPS.</li>
          <li>A Content Security Policy that allows content from this site alone, plus Google Analytics.</li>
          <li>Framing allowed only by this site’s own pages, which use it for template previews.</li>
          <li>No content-type guessing, a limited referrer, and no camera, microphone or location access.</li>
        </ul>
      </Section>
      <Section id="checks" heading="How changes are checked">
        <p>
          Every change reaches the site through a reviewed pull request, and every pull request runs type checks, lint,
          unit and component tests, browser tests on desktop and mobile, accessibility scans of the site’s pages, a
          dependency audit, a scan for committed secrets and a vulnerability scan. Dependencies are kept current through
          automated update requests.
        </p>
      </Section>
      <Section id="downloads" heading="Downloads, packages and starters">
        <p>
          Stylesheets, tokens, design-tool files, templates and starters are generated from the same source as the site
          and served from it over HTTPS. The starters carry the MDS packages built from that source, and the Figma
          plugin runs with network access turned off.
        </p>
      </Section>
      <Section id="adopters" heading="Applications built with MDS">
        <p>
          MDS supplies the visual layer: styles, components, templates and guidance. Each application that adopts it
          owns its own security: authentication, authorization, data handling, hosting and operations, assessed and
          authorized for its own system and impact level.
        </p>
      </Section>
      <Section id="links" heading="Links to other sites">
        <p>
          Links to agencies, standards bodies and other publishers are provided for reference. Those sites have their
          own privacy policies, and a link is not an endorsement of a site or its content.
        </p>
      </Section>
      <Section id="changes" heading="Changes to this policy">
        <p>
          When the site begins handling information differently, or is protected differently, this page changes with
          it, in the same release.
        </p>
      </Section>
    </PolicyPage>
  )
}
